import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, Users, Smartphone, Clock, Heart, Shield, CreditCard, Bell, Calendar, Star, Award, MessageSquare, Gamepad2, Share2, CheckCircle, Zap, Gift, AlertCircle } from 'lucide-react';
import Header from '../components/Header';
import '../styles/index.css';

const PageParents = () => {
  const navigate = useNavigate();
  useEffect(() => { document.title = "EcoLunch - Parents | Interface Famille"; }, []);

  return (
    <div className="home-container">
      <Header currentPage="parents" lang="fr" />

      {/* Hero Parents */}
      <section className="hero-section">
        <div className="hero-background">
          <div className="hero-gradient"></div>
          <div className="hero-grid"></div>
        </div>
        <div className="hero-content">
          <div className="hero-badge animate-fade-in-up">
            <Users size={16} />
            <span>Application Parents</span>
          </div>
          <h1 className="hero-title animate-fade-in-up" style={{ animationDelay: '0.1s' }}>
            <span style={{ color: 'var(--actor-parents)' }}>Parents</span>
          </h1>
          <p className="hero-subtitle animate-fade-in-up" style={{ animationDelay: '0.2s', fontSize: '1.3rem', lineHeight: '1.8' }}>
            Commandez en <strong style={{ color: 'var(--actor-parents)' }}>15 secondes</strong><br />
            pour tous vos enfants sur une seule application
          </p>
          <div className="hero-stats animate-fade-in-up" style={{ animationDelay: '0.4s' }}>
            <div className="stat-item">
              <div className="stat-number">15s</div>
              <div className="stat-label">Pour commander</div>
            </div>
            <div className="stat-divider"></div>
            <div className="stat-item">
              <div className="stat-number">∞</div>
              <div className="stat-label">Enfants gérés</div>
            </div>
            <div className="stat-divider"></div>
            <div className="stat-item">
              <div className="stat-number">100%</div>
              <div className="stat-label">Sécurisé</div>
            </div>
          </div>
        </div>
      </section>

      {/* Manifeste — Pourquoi EcoLunch pour les Parents */}
      <section className="section" data-testid="manifeste-parents">
        <div className="watermark-pattern"></div>
        <div className="section-container">
          <div className="section-header">
            <h2 className="section-title">Pourquoi EcoLunch pour les Parents ?</h2>
          </div>

          <div className="manifeste-tile" data-testid="manifeste-gratuit-parents">
            <div className="manifeste-tile-header">
              <div className="portal-icon" style={{color: '#3B82F6'}}>
                <Gift size={30} />
              </div>
              <h3>Gratuit pour les écoles. Gratuit pour les parents. Durable pour l'écosystème.</h3>
            </div>
            <p>
              EcoLunch sera et restera gratuit pour les écoles. Mieux encore, notre modèle permet également le versement de dons, 
              de sorte qu'une école peut non seulement offrir un service structuré, moderne et sans friction, mais aussi en retirer 
              une retombée positive.
            </p>
            <p>
              Pour les parents, l'utilisation de la plateforme est également gratuite. L'objectif est clair : rendre l'accès au service {' '}
              <strong>simple, fluide, centralisé et sans barrières inutiles</strong>.
            </p>
          </div>

          <div className="manifeste-tile" data-testid="manifeste-coparentalite">
            <div className="manifeste-tile-header">
              <div className="portal-icon" style={{color: '#A855F7'}}>
                <Heart size={30} />
              </div>
              <h3>Conçu pour la vraie vie familiale, incluant la coparentalité</h3>
            </div>
            <p>
              EcoLunch intègre également un portail de coparentalité, parce que la réalité des familles n'est pas uniforme. 
              La plateforme a été pensée pour refléter le quotidien réel des parents, y compris lorsqu'il faut gérer les responsabilités 
              de commande, de paiement, de suivi et d'organisation entre plusieurs adultes.
            </p>
            <p>
              Cette approche fait partie de notre différence : nous ne développons pas une plateforme théorique. 
              Nous développons <strong>une plateforme enracinée dans la réalité d'ici</strong>.
            </p>
          </div>
        </div>
      </section>

      {/* Features Section - TOUTES les fonctionnalités */}
      <section id="features" className="section features-section">
        <div className="watermark-pattern"></div>
        <div className="section-container">
          <div className="section-header">
            <h2 className="section-title">Fonctionnalités Parents</h2>
            <p className="section-subtitle">
              Toutes les fonctionnalités pour gérer facilement les repas de vos enfants
            </p>
          </div>

          <div className="benefits-grid">
            <div className="benefit-card">
              <div className="benefit-icon"><Shield size={32} /></div>
              <h3>Connexion simplifiée</h3>
              <p>Connexion rapide avec Google, Facebook, Apple ID, Microsoft sans avoir besoin de créer un compte manuellement.</p>
            </div>

            <div className="benefit-card">
              <div className="benefit-icon"><AlertCircle size={32} /></div>
              <h3>Gestion allergies intelligente</h3>
              <p>Gestion complète des allergènes de manière intelligente pour assurer la sécurité alimentaire de vos enfants.</p>
            </div>

            <div className="benefit-card">
              <div className="benefit-icon"><Star size={32} /></div>
              <h3>Mémoire préférences</h3>
              <p>Faites plaisir à votre enfant à coup sûr! La plateforme vous permet de noter et garder en mémoire l'appréciation sur les plats de votre enfant.</p>
            </div>

            <div className="benefit-card">
              <div className="benefit-icon"><Bell size={32} /></div>
              <h3>Notifications par courriel</h3>
              <p>Recevez les mises à jour et dates limites et rappel par courriel pour ne jamais manquer une période de commande.</p>
            </div>

            <div className="benefit-card">
              <div className="benefit-icon"><CreditCard size={32} /></div>
              <h3>Paiement sécurisé</h3>
              <p>La seule plateforme qui vous offre la plus grand éventail de modes de paiements sur le marché: Google Pay, Apple Pay, Carte de Crédit, Débit crédit, Virement interac Direct.</p>
            </div>

            <div className="benefit-card">
              <div className="benefit-icon"><CreditCard size={32} /></div>
              <h3>Sezzle - Paiement fractionné</h3>
              <p>Grâce à son partenariat exclusif avec Sezzle Canada, EcoLunch rend le paiement fractionné accessible à tous les parents. Payez en 4 versements échelonnés sur 6 semaines.</p>
            </div>
            <div className="benefit-card">
              <div className="benefit-icon"><Smartphone size={32} /></div>
              <h3>Export & Impression menus</h3>
              <p>Imprimez ou téléchargez directement les repas commandés. Cette affiche peut être utilisée comme rappel visuel pour vous et vos enfants.</p>
            </div>

            <div className="benefit-card">
              <div className="benefit-icon"><Award size={32} /></div>
              <h3>Thèmes Personnalisés</h3>
              <p>Changer le thème d'affichage de votre plateforme. Choisissez parmi: Clair, Sombre, Néon, Neutre.</p>
            </div>

            <div className="benefit-card">
              <div className="benefit-icon"><Calendar size={32} /></div>
              <h3>Synchronisation calendrier</h3>
              <p>Intégrez les repas commandés à votre calendrier numérique, Google, Apple, Microsoft et autres pour une organisation familiale optimale.</p>
            </div>

            <div className="benefit-card">
              <div className="benefit-icon"><Users size={32} /></div>
              <h3>Code couleur par enfant</h3>
              <p>Facilitez la navigation en assignant une couleur différente pour chaque enfant lors de la navigation dans la plateforme.</p>
            </div>

            <div className="benefit-card">
              <div className="benefit-icon"><CheckCircle size={32} /></div>
              <h3>Menu commande rapide</h3>
              <p>Personnalisez le menu complet du traiteur en fonction de la rotation pour adapter l'expérience à vos préférences.</p>
            </div>

            <div className="benefit-card">
              <div className="benefit-icon"><Zap size={32} /></div>
              <h3>C1C (Commande en 1 Clic)</h3>
              <p>Commandez pour la semaine ou pour le mois en UN clic grâce à l'option commande rapide personnalisée.</p>
            </div>

            <div className="benefit-card">
              <div className="benefit-icon"><Clock size={32} /></div>
              <h3>Abonnement repas automatique</h3>
              <p>Une fois vos menus personnalisés vous pouvez créer un abonnement récurrent qui sera effectif tout au long de l'année scolaire.</p>
            </div>

            <div className="benefit-card">
              <div className="benefit-icon"><Smartphone size={32} /></div>
              <h3>Notifications SMS & Push</h3>
              <p>Recevez des alertes par message texte et notifications push directement sur votre téléphone mobile pour ne jamais manquer une information importante.</p>
            </div>

            <div className="benefit-card">
              <div className="benefit-icon"><Heart size={32} /></div>
              <h3>Historique détaillé</h3>
              <p>Consultez l'historique complet des commandes et paiements avec options de filtrage par période, enfant et type de repas.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Coparentalité Section */}
      <section className="section coparent-section">
        <div className="watermark-pattern lighter"></div>
        <div className="section-container">
          <div className="section-header">
            <h2 className="section-title">Coparentalité simplifiée</h2>
            <p className="section-subtitle">
              Pour les familles séparées : partagez la gestion sans la complexité
            </p>
          </div>

          <div className="benefits-grid">
            <div className="benefit-card">
              <div className="benefit-icon"><Share2 size={32} /></div>
              <h3>Garde partagée automatique</h3>
              <p>Configurez les jours de garde. Le parent responsable reçoit les notifications au bon moment.</p>
            </div>

            <div className="benefit-card">
              <div className="benefit-icon"><Users size={32} /></div>
              <h3>Comptes séparés, enfants partagés</h3>
              <p>Chaque parent a son compte indépendant. Les deux voient l'historique des commandes.</p>
            </div>

            <div className="benefit-card">
              <div className="benefit-icon"><CreditCard size={32} /></div>
              <h3>Facturation intelligente</h3>
              <p>Factures séparées selon le calendrier de garde. Transparence totale pour les deux parents.</p>
            </div>

            <div className="benefit-card">
              <div className="benefit-icon"><MessageSquare size={32} /></div>
              <h3>Communication simplifiée</h3>
              <p>Messagerie interne pour coordonner sans friction. Historique des décisions partagé.</p>
            </div>

            <div className="benefit-card">
              <div className="benefit-icon"><CheckCircle size={32} /></div>
              <h3>Approbation conjointe</h3>
              <p>Mode approbation pour les décisions importantes : modifications d'allergies, nouveaux menus, etc.</p>
            </div>

            <div className="benefit-card">
              <div className="benefit-icon"><Calendar size={32} /></div>
              <h3>Calendrier partagé</h3>
              <p>Visualisez le calendrier de garde et les commandes à venir. Synchronisation automatique entre les deux comptes.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Portail Enfant Section */}
      <section className="section game-section">
        <div className="watermark-pattern"></div>
        <div className="section-container">
          <div className="section-header">
            <h2 className="section-title">Portail Enfant - Espace Dédié</h2>
            <p className="section-subtitle">
              Un univers sécurisé et ludique pour impliquer vos enfants
            </p>
          </div>

          <div className="benefits-grid">
            <div className="benefit-card">
              <div className="benefit-icon"><Shield size={32} /></div>
              <h3>Portail Enfant</h3>
              <p>Espace dédié sécurisé où les enfants peuvent consulter, jouer, personnaliser, créer leurs Avatars, commander leurs repas et suivre leurs récompenses.</p>
            </div>

            <div className="benefit-card">
              <div className="benefit-icon"><Gamepad2 size={32} /></div>
              <h3>Jeu de Commande CliC & CroQ™</h3>
              <p>Interface de commande gamifiée pour enfants avec système de points et récompenses. Les enfants choisissent leurs repas de façon ludique et interactive.</p>
            </div>

            <div className="benefit-card">
              <div className="benefit-icon"><Users size={32} /></div>
              <h3>Création d'Avatars</h3>
              <p>Les enfants créent leur propre avatar personnalisé qui apparaît dans leur interface et sur leurs menus imprimés.</p>
            </div>

            <div className="benefit-card">
              <div className="benefit-icon"><Award size={32} /></div>
              <h3>Sélection de Thèmes</h3>
              <p>Laissez vos enfants personnaliser leur expérience avec des thèmes amusants: Sports, Nature, Motocross, Licornes, Espace, Animaux et plus encore.</p>
            </div>

            <div className="benefit-card">
              <div className="benefit-icon"><Star size={32} /></div>
              <h3>Système de Notation Enfant</h3>
              <p>Parents et enfants notent les repas. Les statistiques sont séparées pour les traiteurs, écoles et direction afin de mieux comprendre les préférences de chacun.</p>
            </div>

            <div className="benefit-card">
              <div className="benefit-icon"><Zap size={32} /></div>
              <h3>Défis et Missions</h3>
              <p>Défis alimentaires hebdomadaires pour encourager la découverte de nouveaux aliments. Récompenses et badges à collectionner.</p>
            </div>
          </div>
        </div>
      </section>

      {/* EcoPulse CC Mini-Card */}
      <section className="section" data-testid="ecopulse-mini-parents">
        <div className="watermark-pattern"></div>
        <div className="section-container">
          <div className="ecopulse-mini-card" style={{borderColor: 'rgba(139, 92, 246, 0.4)'}}>
            <div className="ecopulse-mini-header">
              <div className="portal-icon" style={{color: '#84CC16'}}>
                <Zap size={28} />
              </div>
              <div>
                <h3>EcoPulse CC™ <span style={{fontSize: '0.85rem', fontWeight: 500, opacity: 0.7}}>pour les Parents</span></h3>
                <p style={{fontSize: '0.9rem', color: 'var(--color-text-secondary)', margin: 0}}>
                  Une phrase suffit pour tout contrôler
                </p>
              </div>
            </div>
            <div className="ecopulse-mini-commands">
              <div className="ecopulse-mini-cmd">Change le repas d'Emma pour la salade jeudi</div>
              <div className="ecopulse-mini-cmd">Annule vendredi pour les deux enfants</div>
              <div className="ecopulse-mini-cmd">Montre-moi mon solde</div>
              <div className="ecopulse-mini-cmd">Mets Lucas en 4 jours/semaine à partir de lundi</div>
            </div>
            <button onClick={() => navigate('/ecopulse')} className="ecopulse-mini-link">
              Découvrir toutes les commandes EcoPulse <ArrowRight size={16} />
            </button>
          </div>
        </div>
      </section>

      {/* Site Web */}
      <section className="section" style={{paddingTop: '2rem', paddingBottom: '2rem'}}>
        <div className="section-container" style={{textAlign: 'center'}}>
          <a href="https://ecotechsysteme.ca" target="_blank" rel="noopener noreferrer" style={{color: 'var(--color-lime)', fontSize: '1.2rem', fontWeight: 600, textDecoration: 'none', borderBottom: '2px solid rgba(132,204,22,0.3)', paddingBottom: '0.25rem'}}>
            ecotechsysteme.ca
          </a>
        </div>
      </section>

      {/* Footer */}
      <footer className="footer">
        <div className="footer-content">
          <div className="footer-main">
            <div className="footer-brand">
              <div className="footer-logo">
                <img src="/logo-ecolunch.png" alt="EcoLunch" className="footer-logo-image" />
              </div>
              <p className="footer-tagline">
                La plateforme unifiée pour moderniser les repas scolaires au Québec
              </p>
            </div>

            <div className="footer-links">
              <div className="footer-column">
                <h4>Solutions</h4>
                <a onClick={() => navigate('/caterers')}>Pour les Traiteurs</a>
                <a onClick={() => navigate('/parents')}>Pour les Parents</a>
                <a onClick={() => navigate('/schools')}>Pour les Écoles</a>
                <a onClick={() => navigate('/directions')}>Pour les Directions</a>
                <a onClick={() => navigate('/css')}>Pour les CSS</a>
              </div>

              <div className="footer-column">
                <h4>Plateforme</h4>
                <a onClick={() => navigate('/platform')}>Architecture</a>
                <a onClick={() => navigate('/coparentalite')}>Co-Parentalité</a>
                <a onClick={() => navigate('/ecopulse')}>EcoPulse CC</a>
                <a onClick={() => navigate('/ecoloop')}>EcoLoop</a>
              </div>
            </div>
          </div>

          <div className="footer-bottom">
            <div className="footer-legal">
              <span>© 2025 EcoLunch. Tous droits réservés.</span>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default PageParents;